import { useEffect, useMemo, useState } from "react";

import { ClueDialog } from "@/components/Daily/ClueDialog";
import { ClueLabels } from "@/components/Daily/ClueLabels";
import { DailyMap, Probe } from "@/components/Daily/DailyMap";
import { EmptyScreen } from "@/components/Daily/EmptyScreen";
import { EndScreen } from "@/components/Daily/EndScreen";
import { GuessCard } from "@/components/Daily/GuessCard";
import { HelpScreen } from "@/components/Daily/HelpScreen";
import { HelpIcon, PlaneIcon } from "@/components/Daily/icons";
import { CluePill, DistancePill, GuessCounter } from "@/components/Daily/Pills";
import "@/components/Daily/daily.css";
import {
  trackClueOpened,
  trackGuessReviewed,
  trackHowToPlayViewed,
  trackPuzzleViewed,
} from "@/daily/analytics";

import {
  hasSeenHelp,
  loadGuessedIatas,
  markHelpSeen,
  progressKey,
  resetDailyProgress,
  saveGuessedIatas,
} from "@/daily/game";
import {
  DESKTOP_QUERY,
  useKeyboardInset,
  useMediaQuery,
  usePageColour,
} from "@/daily/hooks";
import { possibleAirports, withAirportsHint } from "@/daily/airportsHint";
import { dt } from "@/daily/strings";
import { DailyGuess, DailyPuzzle } from "@/daily/types";
import { useDailyData } from "@/daily/useDailyData";
import { useDailyGame } from "@/daily/useDailyGame";
import { Airport } from "@/models/airport";

// The background of each screen, for the browser's own bars to match. Keep
// in step with daily.css.
const PAGE_COLOUR = {
  map: "#efe4cd",
  won: "#e9c46a",
  lost: "#0e1626",
  loading: "#152238",
};

// Stands in until the airport list arrives.
const NO_AIRPORTS: Airport[] = [];

// How long a tapped guess's distance stays up before it clears itself (ms).
const REVIEW_SHOWS_FOR = 3500;

interface DailyGameProps {
  puzzle: DailyPuzzle;
  airports: Airport[];
}

// The game itself, once today's puzzle and the airport list are in hand.
const DailyGame = ({ puzzle, airports }: DailyGameProps) => {
  // Runs once, before any saved progress is read.
  useState(() => {
    // A previewed draft starts fresh every time it is opened.
    if (puzzle.draft) saveGuessedIatas(progressKey(puzzle), []);
    if (new URLSearchParams(window.location.search).has("reset")) {
      resetDailyProgress(progressKey(puzzle));
    }
    return null;
  });

  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const keyboardInset = useKeyboardInset();
  // The weather clue becomes one that marks possible airports on the map
  // (see airportsHint.ts).
  const gamePuzzle = useMemo(() => withAirportsHint(puzzle), [puzzle]);
  const game = useDailyGame(gamePuzzle, airports);

  // The browser's bars take the colour of the screen showing: the map's
  // paper, or the end screen's gold (a win) or dark navy (a loss).
  usePageColour(
    !game.finished ? PAGE_COLOUR.map : game.won ? PAGE_COLOUR.won : PAGE_COLOUR.lost
  );

  const [helpOpen, setHelpOpen] = useState(() => !hasSeenHelp());

  // Once per visit: the puzzle came up, and whether how-to-play came with it.
  useEffect(() => {
    trackPuzzleViewed(puzzle, game.allGuesses, game.finished);
    if (helpOpen) trackHowToPlayViewed(puzzle, "first_visit");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [openClueNumber, setOpenClueNumber] = useState<number | null>(null);
  // An earlier guess whose pin was tapped: its distance shows again briefly.
  const [reviewedGuess, setReviewedGuess] = useState<DailyGuess | null>(null);

  useEffect(() => {
    if (!reviewedGuess) return;
    const timer = window.setTimeout(
      () => setReviewedGuess(null),
      REVIEW_SHOWS_FOR,
    );
    return () => window.clearTimeout(timer);
  }, [reviewedGuess]);

  const closeHelp = () => {
    markHelpSeen();
    setHelpOpen(false);
  };

  const openClue = (clueNumber: number) => {
    game.clearFeedback();
    setOpenClueNumber(clueNumber);
    trackClueOpened(gamePuzzle, gamePuzzle.clues[clueNumber - 1], clueNumber);
  };

  const reviewGuess = (guess: DailyGuess) => {
    setReviewedGuess(guess);
    trackGuessReviewed(puzzle, guess);
  };

  // The airports the possible-airports clue marks, if the
  // puzzle has that clue.
  const markedAirports = useMemo(
    () =>
      gamePuzzle.clues.some((clue) => clue.type === "possible_airports")
        ? possibleAirports(gamePuzzle, airports)
        : NO_AIRPORTS,
    [gamePuzzle, airports]
  );

  // A finished game shows the end screen, including on a return visit.
  if (game.finished) {
    return <EndScreen puzzle={puzzle} guesses={game.allGuesses} />;
  }

  const newestClue = game.revealedClues[game.revealedClues.length - 1];
  // The newest clue first shows as a pill, then moves up to join the others.
  const showCluePill = game.feedbackPhase === "clue" && !!newestClue;
  const labelledClues = showCluePill
    ? game.revealedClues.slice(0, -1)
    : game.revealedClues;
  // The airports clue's airports drop onto the map once its pill has moved
  // up to join the other clues.
  const airportsClue = game.revealedClues.find((entry) => entry.clue.type === "possible_airports");
  const showingAirports =
    !!airportsClue && !(showCluePill && newestClue.clue.type === "possible_airports");
  const openClueEntry = game.revealedClues.find(
    (entry) => entry.number === openClueNumber,
  );

  const phase = game.feedbackPhase;
  // Reviewing an earlier guess waits its turn behind a guess in progress.
  const reviewing =
    reviewedGuess && (phase === null || phase === "settle")
      ? reviewedGuess
      : null;
  const pillShowing = phase === "distance" || phase === "clue" || !!reviewing;
  const feedbackGuess = game.feedbackGuess;

  // While a guess is checked and its verdict lands, a line reaches toward
  // it on the map and guessing is paused.
  const revealing =
    phase === "checking" || phase === "verdict" || phase === "landing";
  const probe: Probe | null =
    feedbackGuess && (revealing || phase === "distance")
      ? {
          airport: feedbackGuess.airport,
          state:
            phase === "checking"
              ? "drawing"
              : feedbackGuess.band === "correct"
                ? "correct"
                : "wrong",
        }
      : null;

  // What plays after a wrong guess: the distance, then the new clue.
  const feedbackPills = (
    <>
      {game.feedbackPhase === "distance" && game.feedbackGuess && (
        <DistancePill
          guess={game.feedbackGuess}
          large
          onSkip={game.advanceFeedback}
        />
      )}
      {reviewing && (
        <DistancePill
          guess={reviewing}
          large
          review
          onSkip={() => setReviewedGuess(null)}
        />
      )}
      {showCluePill && (
        <CluePill
          clue={newestClue.clue}
          number={newestClue.number}
          isNew
          large
          withDetail
          onOpen={() => openClue(newestClue.number)}
        />
      )}
    </>
  );

  return (
    <div className="ds ds-game">
      <DailyMap
        puzzle={puzzle}
        guesses={game.guesses}
        probe={probe}
        landing={phase === "landing"}
        onGuessClick={reviewGuess}
        asking={game.allGuesses.length === 0}
        isDesktop={isDesktop}
        possibleAirports={showingAirports ? markedAirports : NO_AIRPORTS}
        canGuess={!game.busy && !pillShowing}
        onGuessMarked={(airport) => {
          setReviewedGuess(null);
          game.submitGuess(airport);
        }}
      />

      {/* The map dims slightly while the distance and clue pills are showing. */}
      <div
        className={`ds-dim ${pillShowing ? "ds-dim--on" : ""} ${reviewing ? "ds-dim--tappable" : ""}`}
        aria-hidden="true"
        onClick={() => setReviewedGuess(null)}
      />

      <div className="ds-top">
        <GuessCounter guesses={game.guesses} pending={phase === "checking"} />
        <button
          type="button"
          className="ds-round-button"
          aria-label={dt("howToPlay")}
          onClick={() => {
            setHelpOpen(true);
            trackHowToPlayViewed(puzzle, "help_button");
          }}
        >
          <HelpIcon size={20} />
        </button>
      </div>

      <ClueLabels clues={labelledClues} large={isDesktop} newestNumber={newestClue?.number} onOpen={openClue} />

      {/* Feedback plays large, in the middle of the map. */}
      <div className="ds-stage">{feedbackPills}</div>

      <div
        className="ds-dock"
        style={{ "--ds-keyboard": `${keyboardInset}px` } as React.CSSProperties}
      >
        <GuessCard
          puzzle={puzzle}
          airports={airports}
          guessedIatas={game.allGuesses.map((guess) => guess.airport.iata)}
          checkingIata={
            revealing && feedbackGuess ? feedbackGuess.airport.iata : null
          }
          onGuess={(airport) => {
            setReviewedGuess(null);
            game.submitGuess(airport);
          }}
        />
      </div>

      {openClueEntry && (
        <ClueDialog
          clue={openClueEntry.clue}
          number={openClueEntry.number}
          onClose={() => setOpenClueNumber(null)}
        />
      )}

      {helpOpen && <HelpScreen onClose={closeHelp} />}
    </div>
  );
};

// Daily Service: one flight a day, five guesses at where it lands.
const Daily = () => {
  const { data, retry } = useDailyData();
  // While loading, or on an error, the screen is navy; with no flight to
  // play it is the map's paper. Once the game is up it sets the colour itself.
  usePageColour(
    data.status === "ready"
      ? null
      : data.status === "empty"
        ? PAGE_COLOUR.map
        : PAGE_COLOUR.loading
  );

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Daily Service - Flight Guesser";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  // A game already under way needs the airport list to restore its guesses;
  // a fresh one can start without it.
  const waitingForAirports =
    data.status === "ready" &&
    data.airports === null &&
    loadGuessedIatas(progressKey(data.puzzle)).length > 0;

  if (data.status === "loading" || waitingForAirports) {
    return (
      <div className="ds ds-status" role="status">
        <span className="ds-status__plane" aria-hidden="true">
          <PlaneIcon size={40} />
        </span>
        <p className="ds-status__title">{dt("loading")}</p>
      </div>
    );
  }

  if (data.status === "empty") return <EmptyScreen />;

  if (data.status === "error") {
    return (
      <div className="ds ds-status" role="alert">
        <p className="ds-status__title">{dt("errorTitle")}</p>
        <p className="ds-status__text">{dt("errorText")}</p>
        <button type="button" className="ds-status__button" onClick={retry}>
          {dt("retry")}
        </button>
      </div>
    );
  }

  return (
    <DailyGame
      // A different day's puzzle, or a newer copy of today's, starts afresh.
      key={`${data.puzzle.date}-${data.version}`}
      puzzle={data.puzzle}
      airports={data.airports ?? NO_AIRPORTS}
    />
  );
};

export default Daily;
